/**
 * Persist training sessions in localStorage so they survive navigation and refresh.
 * Sessions are stored per-child per-date so different children/days are independent.
 * The legacy global key is kept for backward-compat migration only.
 */

import type { TrainingSession } from '../types'
import { syncSessions } from '../services/syncService'

/** Legacy key – used only to migrate existing sessions on first load. */
const STORAGE_KEY = 'energymatch-sessions'
/** Legacy per-date key (pre-multi-child) — kept for guest migration only. */
const STORAGE_KEY_BY_DATE_LEGACY = 'energymatch-sessions-v2'

function isValidSession(obj: unknown): obj is TrainingSession {
  if (!obj || typeof obj !== 'object') return false
  const s = obj as Record<string, unknown>
  if (typeof s.id !== 'string') return false
  if (typeof s.isRestDay !== 'boolean') return false
  if (typeof s.time !== 'string') return false
  if (s.intensity !== 'light' && s.intensity !== 'moderate' && s.intensity !== 'hard') return false
  if (typeof s.durationMinutes !== 'number') return false
  // sessionType is optional for backwards compat — old sessions won't have it
  return true
}

/** Normalise a valid session loaded from storage, filling in missing fields added after initial release. */
function normaliseSession(s: TrainingSession): TrainingSession {
  return s.sessionType ? s : { ...s, sessionType: 'training' }
}

function storageKey(childId: string): string {
  return `energymatch-sessions-v2-${childId}`
}

function getSessionsByDate(childId: string): Record<string, TrainingSession[]> {
  try {
    const raw = localStorage.getItem(storageKey(childId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: Record<string, TrainingSession[]> = {}
    for (const [key, val] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(val)) {
        const sessions = (val as unknown[]).filter(isValidSession).map(normaliseSession)
        if (sessions.length > 0) out[key] = sessions
      }
    }
    return out
  } catch {
    return {}
  }
}

export function getSessionsForDate(childId: string, dateKey: string): TrainingSession[] {
  return getSessionsByDate(childId)[dateKey] ?? []
}

export function setSessionsForDate(
  userId: string,
  childId: string,
  dateKey: string,
  sessions: TrainingSession[]
): void {
  try {
    const all = getSessionsByDate(childId)
    all[dateKey] = sessions
    localStorage.setItem(storageKey(childId), JSON.stringify(all))
  } catch {
    // quota or disabled
  }
  syncSessions(userId, childId, dateKey, sessions)
}

export function clearSessionsForDate(childId: string, dateKey: string): void {
  try {
    const all = getSessionsByDate(childId)
    delete all[dateKey]
    localStorage.setItem(storageKey(childId), JSON.stringify(all))
  } catch {
    // ignore
  }
}

// ── Legacy global functions (kept for migration) ──────────────────────────────

export function getStoredSessions(): TrainingSession[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return null
    const sessions = parsed.filter(isValidSession)
    return sessions.length > 0 ? sessions : null
  } catch {
    return null
  }
}

/** Read all sessions from the old per-date (pre-multi-child) key. Used for guest migration. */
export function getLegacySessionsByDate(): Record<string, TrainingSession[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_BY_DATE_LEGACY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: Record<string, TrainingSession[]> = {}
    for (const [key, val] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(val)) {
        const sessions = (val as unknown[]).filter(isValidSession).map(normaliseSession)
        if (sessions.length > 0) out[key] = sessions
      }
    }
    return out
  } catch {
    return {}
  }
}
